import React, { useEffect, useState } from "react";
import classes from "./loginPage.module.css";
import useAuth from "../../hooks/useAuth";
import { Link, useNavigate } from "react-router-dom";
import { notifyError } from "../../utils";
import Cookies from "js-cookie";
import axios from "axios";
import {
  loginService,
  registerService,
  sendEmailOTPService,
} from "../../services";
import { notifySuccess } from "../../utils";
import Footer from "../../components/footer";
import { ToastContainer } from "react-toastify";

const LoginPage = ({ removeContainer, removeFooter, popupView }) => {
  const [activeTab, setActiveTab] = useState("login");
  const [formData, setFormData] = useState({
    username: "",
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    mobileNumber: "",
    email: "",
    password: "",
    userType: "user", // Default is user
    isArtist: true, // Added isArtist checkbox state (unchecked by default)
    otp: "",
    gender: "male",
  });

  const [loginFormData, setLoginFormData] = useState({
    email: "",
    password: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [error, setError] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [showPassword, setShowPassword] = useState(false); // Add password visibility state

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Remove spaces from username input
    if (name === "username") {
      const valueWithoutSpaces = value.replace(/\s/g, "");
      setFormData({
        ...formData,
        [name]: valueWithoutSpaces,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Add a specific handler for the checkbox
  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setFormData({
      ...formData,
      isArtist: isChecked,
      userType: isChecked ? "artist" : "user", // Update userType based on checkbox
    });
  };

  const handleInputChangeLogin = (e) => {
    const { name, value } = e.target;
    setLoginFormData({
      ...loginFormData,
      [name]: value,
    });
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(loginService, {
        email: loginFormData.email,
        password: loginFormData.password,
      });

      if (response.status === 200) {
        const { user, tokens } = response?.data?.data;

        // Extract tokens
        const accessToken = tokens.access.token;
        const refreshToken = tokens.refresh.token;

        // Set cookies with a 1-day expiry
        Cookies.set("accessToken", accessToken, {
          expires: 1,
          secure: true,
          sameSite: "Strict",
        });
        Cookies.set("refreshToken", refreshToken, {
          expires: 1,
          secure: true,
          sameSite: "Strict",
        });

        // Store user data (optional)
        Cookies.set("user", JSON.stringify(user), { expires: 1 });

        notifySuccess(`Welcome ${user.username}`);

        const timerId = setTimeout(() => {
          if (user?.type === "artist" && !user?.onboarded) {
            navigate("/onboarding");
          } else if (!popupView) navigate("/");
          window.location.reload();
          setIsLoading(false);
        }, 2000);

        return { accessToken, refreshToken, user };
      } else {
        notifyError("Invalid email or password");
        setIsLoading(false);
        return null;
      }
    } catch (error) {
      setIsLoading(false);
      notifyError("Invalid email or password");
      return null;
    }
  };

  // Start timer for OTP expiration
  const startOtpTimer = () => {
    setOtpTimer(120); // 2 minutes countdown
  };

  // Handle timer countdown
  useEffect(() => {
    if (otpTimer > 0) {
      const interval = setInterval(() => {
        setOtpTimer((prevTimer) => prevTimer - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [otpTimer]);

  // Format timer for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleSendOtp = async () => {
    if (!formData.email) {
      setError("Please enter your email address");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSendingOtp(true);
    try {
      const response = await axios.post(sendEmailOTPService, {
        email: formData.email,
      });

      if (response.status === 200 || response.status === 201) {
        setOtpSent(true);
        setShowOtpInput(true);
        setError(null);
        startOtpTimer();
        notifySuccess("OTP sent to your email");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to send OTP";
      notifyError(errorMessage);
      setError(errorMessage);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Validate form fields first
    if (!formData.username.trim()) {
      setError("Username is required");
      setIsLoading(false);
      return;
    }
    if (!formData.firstName.trim()) {
      setError("First name is required");
      setIsLoading(false);
      return;
    }
    if (!formData.lastName.trim()) {
      setError("Last name is required");
      setIsLoading(false);
      return;
    }
    if (!formData.dateOfBirth) {
      setError("Date of birth is required");
      setIsLoading(false);
      return;
    }
    if (!formData.mobileNumber.trim()) {
      setError("Mobile number is required");
      setIsLoading(false);
      return;
    }
    if (!formData.email.trim()) {
      setError("Email is required");
      setIsLoading(false);
      return;
    }
    if (!formData.password.trim()) {
      setError("Password is required");
      setIsLoading(false);
      return;
    }
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      setIsLoading(false);
      return;
    }
    if (!formData.gender) {
      setError("Gender is required");
      setIsLoading(false);
      return;
    }

    // Check if OTP is sent and entered
    if (!otpSent) {
      setError("Please verify your email with OTP first");
      setIsLoading(false);
      return;
    }
    if (!formData.otp.trim()) {
      setError("Please enter the OTP sent to your email");
      setIsLoading(false);
      return;
    }

    const payload = {
      username: formData.username,
      first_name: formData.firstName,
      last_name: formData.lastName,
      dob: formData.dateOfBirth,
      phone_number: formData.mobileNumber,
      email: formData.email,
      password: formData.password,
      type: formData.isArtist ? "artist" : "user", // Use the checkbox value for type
      otp: formData.otp,
      gender: formData.gender,
    };

    try {
      const response = await axios.post(registerService, payload, {
        headers: { "Content-Type": "application/json" },
      });

      console.log("Registration response:", response);

      if (response.status === 200 || response.status === 201) {
        setActiveTab("login");
        notifySuccess("Registration Successful");

        // Reset form data
        setFormData({
          username: "",
          firstName: "",
          lastName: "",
          dateOfBirth: "",
          mobileNumber: "",
          email: "",
          password: "",
          userType: "user",
          isArtist: false, // Reset the checkbox
          otp: "",
          gender: "male",
        });
        setOtpSent(false);
        setShowOtpInput(false);
        setShowPassword(false); // Reset password visibility
      }
    } catch (err) {
      console.log("Registration error:", err);
      const errorMessage =
        err.response?.data?.errors[0]?.message || "Registration failed";

      // Check if error is related to OTP
      if (
        err.response?.data?.error === "INVALID_OTP" ||
        errorMessage.toLowerCase().includes("otp") ||
        errorMessage.toLowerCase().includes("verification")
      ) {
        notifyError("Invalid or expired OTP. Please try again.");
        setError("Invalid or expired OTP. Please try again.");
      } else {
        notifyError(errorMessage);
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [isAuthenticated, navigate]);

  // Reset states when changing tabs
  useEffect(() => {
    setOtpSent(false);
    setShowOtpInput(false);
    setOtpTimer(0);
    setError(null);
    setShowPassword(false); // Reset password visibility when changing tabs
  }, [activeTab]);

  const AuthCard = () => {
    return (
      <div className={removeContainer ? "" : classes.authCard}>
        <div className={classes.logoContainer}>
          <h1>ArtistNextDoor</h1>
        </div>

        <div className={classes.tabs}>
          <button
            className={`${classes.tabButton} ${
              activeTab === "login" ? classes.activeTab : ""
            }`}
            onClick={() => setActiveTab("login")}
          >
            Login
          </button>
          <button
            className={`${classes.tabButton} ${
              activeTab === "register" ? classes.activeTab : ""
            }`}
            onClick={() => setActiveTab("register")}
          >
            Register
          </button>
        </div>

        {error && <div className={classes.errorMessage}>{error}</div>}

        {activeTab === "login" ? (
          <div className={classes.tabContent}>
            <h2>Welcome back</h2>
            <p className={classes.subtitle}>Log in to your account</p>

            <form onSubmit={handleLoginSubmit} className={classes.form}>
              <div className={classes.inputGroup}>
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={loginFormData.email}
                  onChange={handleInputChangeLogin}
                  required
                  placeholder="Enter your email"
                />
              </div>

              <div className={classes.inputGroup}>
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={loginFormData.password}
                  onChange={handleInputChangeLogin}
                  required
                  placeholder="Enter your password"
                />
              </div>

              <div className={classes.forgotPassword}>
                <Link to="/forgot-password">Forgot password?</Link>
              </div>

              <button
                type="submit"
                className={classes.actionButton}
                disabled={isLoading}
              >
                {isLoading ? "Logging in..." : "Log In"}
              </button>
            </form>

            <div className={classes.divider}>
              <span>or</span>
            </div>

            <button className={classes.googleButton} disabled>
              <div className={classes.googleIcon}>G</div>
              Continue with Google (Coming Soon)
            </button>
          </div>
        ) : (
          <div className={classes.tabContent}>
            <h2>Create an account</h2>

            {/* Replace select dropdown with checkbox */}
            <div className={classes.checkboxGroup}>
              <label className={classes.checkboxLabel}>
                <input
                  type="checkbox"
                  name="isArtist"
                  checked={formData.isArtist}
                  onChange={handleCheckboxChange}
                  className={classes.checkboxInput}
                />
                Register as Artist
              </label>
            </div>

            <form onSubmit={handleRegisterSubmit} className={classes.form}>
              <div className={classes.inputRow}>
                <div className={classes.inputGroup}>
                  <label htmlFor="username">Username</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    placeholder="Choose a username"
                  />
                </div>

                <div className={classes.inputGroup}>
                  <label htmlFor="mobileNumber">Mobile Number</label>
                  <input
                    type="tel"
                    id="mobileNumber"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your number"
                  />
                </div>
              </div>
              <div className={classes.inputRow}>
                <div className={classes.inputGroup}>
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your first name"
                  />
                </div>

                <div className={classes.inputGroup}>
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your last name"
                  />
                </div>
              </div>
              <div className={classes.inputRow}>
                <div className={classes.inputGroup}>
                  <label htmlFor="dateOfBirth">Date of Birth</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className={classes.inputGroup}>
                  <label htmlFor="gender">Gender</label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    required
                    className={classes.selectInputGender}
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>
              <div className={classes.inputRow}>
                <div className={classes.inputGroup}>
                  <label htmlFor="regEmail">Email</label>
                  <div className={classes.emailWithButton}>
                    <input
                      type="email"
                      id="regEmail"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your email"
                      className={otpSent ? classes.verifiedInput : ""}
                      disabled={otpSent}
                    />
                    {!otpSent && (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        className={classes.sendOtpButton}
                        disabled={isSendingOtp || !formData.email}
                      >
                        {isSendingOtp ? "Sending..." : "Send OTP"}
                      </button>
                    )}
                    {otpSent && (
                      <div className={classes.emailVerifiedBadge}>
                        {otpTimer > 0 ? (
                          <span>OTP valid for: {formatTime(otpTimer)}</span>
                        ) : (
                          <button
                            type="button"
                            onClick={handleSendOtp}
                            className={classes.resendButton}
                            disabled={isSendingOtp}
                          >
                            Resend OTP
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className={classes.inputGroup}>
                  <label htmlFor="regPassword">Password</label>
                  <div className={classes.passwordInputContainer}>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="regPassword"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      placeholder="Create a password"
                      minLength="8"
                      className={classes.passwordInput}
                    />
                    <button
                      type="button"
                      className={classes.eyeButton}
                      onClick={togglePasswordVisibility}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? (
                        // Eye slash icon (hidden)
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        // Eye icon (visible)
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {showOtpInput && (
                <div className={classes.inputGroup}>
                  <label htmlFor="otp">Enter OTP</label>
                  <input
                    type="text"
                    id="otp"
                    name="otp"
                    value={formData.otp}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter the code sent to your email"
                    className={classes.otpInput}
                    maxLength="6"
                  />
                </div>
              )}

              <button
                type="submit"
                className={classes.actionButton}
                disabled={isLoading}
              >
                {isLoading ? "Creating Account..." : "Create Account"}
              </button>
            </form>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <ToastContainer />
      <div className={removeContainer ? "" : classes.authContainer}>
        {AuthCard()}
      </div>
      {removeFooter ? null : <Footer />}
    </>
  );
};

export default LoginPage;
